import React, { useEffect } from 'react';
import { Chart } from 'react-google-charts';
import { Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { loadInvoiceStatusCounts } from '../../../../redux/features/dashboard/actions';
import { useAppSelector } from '../../../../redux/hooks';

interface IInvoiceStatusGraphProps {
  /** Heading shown above the chart, e.g. "This Month" or "Last Month". */
  label: string;
  /** 0 for the current month, 1 for last month, etc. */
  monthsAgo: number;
}

// Validated categorical palette (dataviz skill): blue (Draft), yellow (Published), green (Sent).
const CHART_COLORS = ['#2a78d6', '#eda100', '#008300'];

/**
 * Card component that displays invoice counts for a specific month, broken down by status
 * (Draft, Published, Sent), alongside summary counters and a Google Charts donut chart.
 *
 * On mount, dispatches {@link loadInvoiceStatusCounts} to populate the Redux `dashboard` slice
 * for the given `monthsAgo` offset. Renders a `CircularProgress` spinner until data arrives.
 */
export const InvoiceStatusGraph: React.FC<IInvoiceStatusGraphProps> = ({ label, monthsAgo }) => {
  const counts = useAppSelector((state) => state.dashboard.invoiceStatusCountsByMonthsAgo[monthsAgo]);

  useEffect(() => {
    loadInvoiceStatusCounts(monthsAgo);
  }, [monthsAgo]);

  return (
    <Card elevation={3}>
      <CardContent>
        <Typography variant="h6">Invoices {label}</Typography>
        {
          !counts
            ? <CircularProgress size={24} />
            : (
              <>
                <Box sx={{ display: 'flex', gap: 4, mb: 1 }}>
                  <Box>
                    <Typography variant="h4">{counts.total}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Invoices</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4">{counts.draft}</Typography>
                    <Typography variant="body2" color="text.secondary">Draft Invoices</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4">{counts.published}</Typography>
                    <Typography variant="body2" color="text.secondary">Published Invoices</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4">{counts.sent}</Typography>
                    <Typography variant="body2" color="text.secondary">Sent Invoices</Typography>
                  </Box>
                </Box>
                <Chart
                  chartType="PieChart"
                  height="250px"
                  loader={<div>Loading Chart...</div>}
                  data={[
                    ['Status', 'Invoices'],
                    ['Draft', counts.draft],
                    ['Published', counts.published],
                    ['Sent', counts.sent],
                  ]}
                  options={{
                    colors: CHART_COLORS,
                    pieHole: 0.4,
                    pieSliceText: 'value',
                    // Without this, a 0-count slice (e.g. no Published invoices) gets silently
                    // regrouped into a generic "Other" slice instead of showing as its own 0.
                    sliceVisibilityThreshold: 0,
                    legend: { position: 'bottom' },
                  }}
                />
              </>
            )
        }
      </CardContent>
    </Card>
  );
};
